import config from './config';

/**
 * Describes the valid values for the `resolveTo` configuration.
 * - `smart`: Get the PDP for a platform if the route matches a platform.
 * Otherwise, get the PDP for a product.
 * - `product`: Always resolve to the PDP for the product.
 * Fail if the route does not match a product.
 * - `platform`: Always resolve to the PDP for the platform.
 * If encountering a product, try to resolve to the product's platform.
 * If the platform cannot be resolved, the request should fail.
 */
export type RoutingResolveTo = 'smart' | 'product' | 'platform';

/**
 * Describes the valid positions for strikethrough pricing display.
 * - `left`: Show strikethrough price to the left of the sale price.
 * - `right`: Show strikethrough price to the right of the sale price.
 */
export type StrikethroughPosition = 'left' | 'right';

/**
 * Describes the valid groupName positions for product display.
 */
export type GroupNamePosition = 'above' | 'inline';

/**
 * Describes the valid styles for discount text formatting.
 * - `percentage`: Display as "(X% off)" format (default for NA).
 * - `originalPrice`: Display as "Original Price XX" format (used in EMEA).
 */
export type DiscountTextStyle = 'percentage' | 'originalPrice';

/**
 * Describes the placement of breadcrumbs on the PDP.
 * - `belowPanel`: Show breadcrumbs below the ProductDetailsPanel (default).
 * - `abovePanel`: Show breadcrumbs above the ProductDetailsPanel.
 */
export type BreadcrumbPlacement = 'belowPanel' | 'abovePanel';

const groupMap: Record<string, string> = {
  men: 'Men',
  women: 'Women',
  home: 'Home',
  infants: 'Babies',
  'kids-baby': 'Babies',
  'kids-youth': 'Kids',
  'older-kids': 'Kids',
  'little-kids': 'Kids',
  'all-gender': 'All Gender'
};

const product = config({
  /** Defaults for all sites and locales. */
  default: {
    /** Default settings for all languages. */
    default: {
      /**
       * Whether the preview mode is enabled on products.
       */
      isPreviewEnabled: true,

      /** On the PDP should the carousel be shown within the variation panel. */
      isCarouselShownWithinVariationsPanelOnMobile: true,
      /**
       * Displays content in and alternate PDP location and not among the accordions.
       */
      displayGenericContent: true,

      /**
       * Display product images as a grid that can be clicked to view larger as a slider.
       */
      displayImageGrid: true,

      /** Display the breadcrumbs on PDP. */
      displayBreadcrumbs: true,

      /** Placement of breadcrumbs on PDP. */
      breadcrumbPlacement: 'abovePanel' as BreadcrumbPlacement,

      /**
       * Product info (ie. description, features, etc.) is typically rendered
       * out via this component, after the Product Details content. We can
       * instead pass the product info to the ProductDetailsPanel component,
       * so that it can be displayed within an alternate component context.
       */
      displayProductDetails: true,

      /**
       * Show the short description on the PDP.
       */
      showShortDescription: false,

      /** Size Selector settings. */
      sizeSelector: {
        /**
         * Whether the size chart link should be displayed in the size selector.
         */
        showSizeChartLink: false,

        /**
         * The upper bound of sizes that exist only in the Toddler (C/T) range.
         *
         * Children's shoe sizing: Toddler (C/T) covers sizes 0-13, Youth (K/Y)
         * covers sizes 1-7. Sizes 8-13 are exclusively toddler. When a kids
         * product has sizes both above this threshold AND at/below it, the
         * product spans the toddler/youth boundary and the smaller sizes should
         * be treated as youth.
         */
        kidsToddlerOnlyThreshold: 7
      },

      /** Quickview settings. */
      quickView: {
        /**
         * Controls whether the quickview button should show on product tiles.
         */
        show: false
      },

      /** Color variants counter. */
      colorVariantsCounter: {
        /**
         * Controls whether the color variants counter should show on product tiles.
         */
        show: false
      },

      swatches: {
        /** Enables image swatches on the PDP. */
        enableImageSwatchesPDP: true,
        /** Enable swatch slider on mobile for the PDP. */
        enableMobileSwatchSlider: true
      },

      recommendations: {
        isSliderEnabled: true,
        colorSelectorEnabled: false,
        hoverIntentEnabled: true
      },

      /** Info sections that should be displayed on the PDPs. */
      details: {
        /**
         * Settings related to the video player.
         */
        videoSettingsDesktop: { autoplay: true, muted: true, loop: true },
        videoSettingsMobile: { autoplay: true, muted: true, loop: true },
        /**
         * Enables the content blocks section on PDP.
         *
         * Takes priority over each content block individually, so in order to display at least one
         * content block, this must be enabled.
         */
        iconBlocks: {
          enabled: true,
          /** Enables locally modal functionality on PDP. */
          locally: { enabled: true },
          /** Enables gladly help chat on PDP. */
          helpChat: { enabled: true },
          /** Enables free shipping modal information on PDP. */
          freeShipping: { enabled: true }
        },
        /**
         * Show the short description inside the details section of the PDP accordion.
         */
        showShortDescriptionInAccordionDetails: true,
        /**
         * Controls the Description accordion section. We can either enable or
         * expand the section.
         */
        descriptionLong: {
          /** Whether the section is enabled. */
          enabled: true,
          /** Whether the section is expanded by default. */
          expanded: true,
          /** The position of the section. */
          position: 2
        },
        /**
         * Controls the Features & Benefits accordion section. We can either enable or
         * expand the section.
         */
        featuresAndBenefits: {
          /** Whether the section is enabled. */
          enabled: true,
          /** Whether the section is expanded by default. */
          expanded: true,
          /** The position of the section. */
          position: 1
        },
        /**
         * Controls the Size & Fit accordion section. We can either enable or
         * expand the section.
         */
        sizeAndFit: {
          /** Whether the section is enabled. */
          enabled: true,
          /** Whether the section is expanded by default. */
          expanded: false,
          /** The position of the section. */
          position: 3
        },
        /**
         * Controls the Responsibility accordion section. We can either enable or
         * expand the section.
         */
        responsibility: {
          /** Whether the section is enabled. */
          enabled: false,
          /** Whether the section is expanded by default. */
          expanded: false,
          /** The position of the section. */
          position: 4
        },
        /**
         * Controls the Care & Cleaning accordion section. We can either enable or
         * expand the section.
         */
        careAndCleaning: {
          /** Whether the section is enabled. */
          enabled: true,
          /** Whether the section is expanded by default. */
          expanded: false,
          /** The position of the section. */
          position: 4,
          /** The href for the Care & Cleaning page. */
          href: '/blog/how-to-clean-teva-sandals'
        },
        descriptionShort: {
          /** Whether the section is enabled. */
          enabled: false
        },

        /**
         * Settings related to gender adaptive display.
         * @see `GenderAdaptiveProductVM`
         */
        genderAdaptiveDisplay: {
          /**
           * If enabled, PDPs will display a gender selector for models with
           * all-gender sizing. The sizes displayed on the PDP will adapt to the
           * selected gender.
           */
          enabled: true,

          /** If gender adaptive display should be used on quickview as well. */
          useOnQuickview: false,

          /**
           * The gender that will be selected by default on pages using
           * gender adaptive display.
           */
          defaultGenderSelection: 'men'
        },
        /**
         * Whether the product's above cart details should be displayed above the 'Add to Cart' button.
         */
        showAboveATCDetails: true,
        /**
         * Whether the product's content asset should be displayed below the 'Add to Cart' button.
         */
        showBelowATCContent: true
      },

      /** Lifestyle images settings. */
      lifestyleImages: {
        /**
         * Controls whether lifestyles images (if present) should
         * be displayed on the PDP.
         */
        show: false
      },

      wishlist: {
        /** Enables all wishlist functionality. */
        enable: false
      },

      /** Product parsing settings. */
      parsing: {
        images: {
          /**
           * Maximum time (in milliseconds) an aspect ratio get
           * can take before timing out.
           */
          aspectRatioCalculationTimeout: 5000,

          /**
           * The default aspect ratio to include for product images.
           * This value will be used if the aspect ratio get fails.
           *
           * The current value of `1.2629931120851596` was determined
           * considering that most (if not all) of the product images
           * in the CDN have a size of `2000 x 1597`. This is roughly
           * equal to a **5:4 aspect ratio**.
           */
          defaultAspectRatio: 1.2629931120851596
        }
      },

      plp: {
        /**
         * Whether or not to show the gender on the product tiles.
         * Example: 'Men's', 'Women's', 'All Gender'.
         */
        showGenderOnProductTiles: true,

        /**
         * Determines if it will use the quick on hover color selector or the selection one when
         * displaying a ProductTile in the PLP.
         */
        useQuickColorSelector: false,

        /**
         * The number of swatches to be displayed on mobile.
         */
        mobileSwatchesPerView: 3,

        /**
         * Whether or not to show the "Best for:" text on the product tiles.
         */
        showBestForText: true,

        /**
         * Show percentage off text under pricing on product tiles
         */
        showPercentageOffText: true
      },

      /** Routing settings. */
      routing: {
        /** PDP specific routing settings. */
        pdp: {
          /**
           * Determines how to resolve a PDP route (`/p/*`).
           * @see {@link RoutingResolveTo} for possible values and their behavior.
           */
          resolveTo: 'smart' as RoutingResolveTo,

          /**
           * A map of product IDs or style numbers to redirect to a different page.
           * The key is the ID/styleNumber (last segment of the path), and the value is the destination path.
           * @example { '737872927738': '/p/gift-card' }
           */
          redirects: { '737872927738': '/p/gift-card' } as Record<
            string,
            string
          >,

          /**
           * The TTL (in seconds) to use for caching the PDP on the client upon success.
           *
           * It is recommended to set this TTL to be shorter than the CDN TTL,
           * since the CDN cache can be manually invalidated sooner than the client cache.
           */
          clientTTL: 60 * 60, // 1 hour in seconds

          /**
           * The TTL (in seconds) to use for caching the PDP on a CDN upon success.
           *
           * It is recommended to set this TTL both short enough that the
           * inventory data included in cached PDPs doesn't get too old, but long
           * enough that the perceived performance is not greatly impacted.
           */
          cdnTTL: 24 * 60 * 60, // 24 hours (1 day) in seconds

          /**
           * The TTL (in seconds) to use upon failure when routing to the PDP.
           * This is shared by the client and the CDN.
           *
           * It is recommended that this TTL is quite short so that a fluke error
           * doesn't persist for too long. However, we still want to cache the
           * error for a short time to prevent a flood of requests.
           */
          failureTTL: 5 // 5 seconds
        }
      },

      /** Categories to show in the size chart. */
      sizeChartCategoriesToShow: {
        men: true,
        women: true,
        unisex: true,
        youth: false,
        kids: false,
        infants: true,
        toddlers: true,
        littleKids: true,
        bigKids: true
      },
      unisexSizingMeasurements: {
        us: false,
        unisexMen: true,
        unisexWomen: true,
        heelToe: true,
        uk: true,
        eu: true,
        jp: true,
        mx: true,
        cn: true
      },
      /**
       * Maps product group values from SFCC to display names for SEO metadata titles.
       * Used when constructing fallback page titles.
       */
      groupDisplayNames: groupMap,
      /** The background color used for product images on various parts of the site. */
      background: {
        /** The tile background color. */
        tile: '#f3f3f3'
      },
      /** Configurations related to product groups. */
      group: {
        /**
         * Whether or not to display the group name of the product.
         */
        displayGroupName: { pdp: true, plp: true },
        /**
         * Position of the group name on product display pages.
         */
        groupNamePosition: {
          pdp: 'inline' as GroupNamePosition,
          plp: 'inline' as GroupNamePosition
        }
      },
      /** Product pricing display settings. */
      pricing: {
        /** Strikethrough price position settings. */
        strikethroughPosition: {
          /** Position on Product Display Page (PDP). */
          pdp: 'right' as StrikethroughPosition,
          /** Position on Product List Page (PLP). */
          plp: 'right' as StrikethroughPosition
        },
        /**
         * Discount text format settings.
         *
         * WARNING: Do not use null or undefined when unneeded - these values
         * cause issues with ConfigModel. Use empty string '' for optional/unset
         * values instead.
         */
        discountTextFormat: {
          /**
           * Display style: 'percentage' for "(X% off)" or 'originalPrice' for
           * "Original Price XX".
           */
          style: 'percentage' as DiscountTextStyle,
          /** Optional custom CSS class. Use empty string when not needed. */
          className: '' as string
        },
        /**
         * Whether to show strikethrough when original and current prices differ.
         * Defaults to `true`.
         */
        showStrikethrough: { each: true, total: false }
      }
    }
  },
  'SANUK-US': {
    default: { routing: { pdp: { resolveTo: 'product' as RoutingResolveTo } } }
  },
  'AHNU-US': {
    default: {
      isPreviewEnabled: false,
      displayImageGrid: false,
      /** Display generic pdp content. */
      displayGenericContent: false,
      /** Show the short description. */
      showShortDescription: true,
      /** Display the breadcrumbs on PDP. */
      displayBreadcrumbs: false,
      /** Placement of breadcrumbs on PDP. */
      breadcrumbPlacement: 'belowPanel' as BreadcrumbPlacement,
      /** Display the product details in the main pdp section. */
      displayProductDetails: false,
      sizeSelector: { showSizeChartLink: true },
      colorVariantsCounter: { show: true },
      recommendations: { hoverIntentEnabled: false },
      plp: { useQuickColorSelector: true, showBestForText: false },
      /** Info sections that should be displayed on the PDPs. */
      details: {
        iconBlocks: {
          enabled: false,
          locally: { enabled: false },
          helpChat: { enabled: false },
          freeShipping: { enabled: false }
        },
        showShortDescriptionInAccordionDetails: false,
        /**
         * Controls the Description accordion section. We can either enable or
         * expand the section.
         */
        descriptionLong: {
          /** Whether the section is enabled. */
          enabled: true,
          /** Whether the section is expanded by default. */
          expanded: false,
          /** The position of the section. */
          position: 1
        },
        /**
         * Controls the Features & Benefits accordion section. We can either enable or
         * expand the section.
         */
        featuresAndBenefits: {
          /** Whether the section is enabled. */
          enabled: true,
          /** Whether the section is expanded by default. */
          expanded: false,
          /** The position of the section. */
          position: 2
        },
        /**
         * Controls the Responsibility accordion section. We can either enable or
         * expand the section.
         */
        responsibility: {
          /** Whether the section is enabled. */
          enabled: true,
          /** Whether the section is expanded by default. */
          expanded: false,
          /** The position of the section. */
          position: 4
        },
        /**
         * Controls the Care & Cleaning accordion section. We can either enable or
         * expand the section.
         */
        careAndCleaning: {
          /** Whether the section is enabled. */
          enabled: false,
          /** Whether the section is expanded by default. */
          expanded: false,
          /** The position of the section. */
          position: 5,
          /** The href for the Care & Cleaning page. */
          href: '/blog/how-to-clean-sandals'
        },
        descriptionShort: {
          /** Whether the section is enabled. */
          enabled: true
        }
      },
      /** Lifestyle images settings. */
      lifestyleImages: { show: true },
      /** Categories to show in the size chart. */
      sizeChartCategoriesToShow: {
        men: true,
        women: true,
        unisex: false,
        youth: false,
        kids: false,
        infants: false,
        toddlers: false,
        littleKids: false,
        bigKids: false
      },
      background: { tile: 'transparent' },
      /** Configurations related to product groups. */
      group: {
        groupNamePosition: {
          pdp: 'above' as GroupNamePosition,
          plp: 'inline' as GroupNamePosition
        }
      },
      pricing: {
        strikethroughPosition: {
          pdp: 'right' as StrikethroughPosition,
          plp: 'right' as StrikethroughPosition
        },
        showStrikethrough: { each: true, total: true }
      },
      routing: { pdp: { redirects: {} as Record<string, string> } }
    }
  },
  'TEVA-US': {
    default: { pricing: { showStrikethrough: { each: true, total: true } } },
    EMEA: {
      default: {
        /** EMEA pricing display overrides. */
        pricing: {
          discountTextFormat: {
            /** EMEA shows "Original Price XX" instead of percentage. */
            style: 'originalPrice' as DiscountTextStyle,
            /** EMEA specific CSS class. */
            className: 'emea'
          }
        }
      }
    },
    'UGG_COLLAB-US': { default: { details: { quickView: { show: true } } } },
    'UGG-US': { default: { details: { quickView: { show: true } } } }
  }
});

export default product;
